import Button from '@components/atoms/button'
import { FastlyImage } from '@components/common/image'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { ActivityCardType } from '@lib/types/course-landing-page'
import { useState } from 'react'

const IMAGE_PATH = '/images/courses/'
const INITIAL_VISIBLE_COUNT = 12

const ACTIVITIES: Record<string, ActivityCardType[]> = {
  PUBLIC_SPEAKING_GRADE_3_5: [
    {
      activityName: 'Spotlight introductions',
      activityDescription:
        'Build a confident self-introduction with posture, eye contact, and a calm voice. Practice a clear hello, name, and one key detail about yourself.',
      activityImage: 'spotlight-introductions',
      altText: 'Spotlight introductions',
    },
    {
      activityName: 'One-minute speak sprint',
      activityDescription:
        'Speak on a topic for one minute with quick thinking and clear flow. Learn to start strong, stay on track, and finish confidently.',
      activityImage: 'one-minute-speak-sprint',
      altText: 'One-minute speak sprint',
    },
    {
      activityName: 'Story ladder',
      activityDescription:
        'Turn a simple idea into a story with a beginning, middle, and end. Add details and transitions to make your story smooth and engaging.',
      activityImage: 'story-ladder',
      altText: 'Story ladder',
    },
    {
      activityName: 'Voice modulation studio',
      activityDescription:
        'Practice pace, pause, and tone to sound confident and expressive. Learn when to slow down, speed up, and stress key words.',
      activityImage: 'voice-modulation-studio',
      altText: 'Voice modulation studio',
    },
    {
      activityName: 'Emotion and expression drill',
      activityDescription:
        'Match emotions with your voice, face, and gestures while speaking. Make your delivery sound happy, serious, excited, or calm.',
      activityImage: 'emotion-and-expression-drill',
      altText: 'Emotion and expression drill',
    },
    {
      activityName: 'Impromptu topic spinner',
      activityDescription:
        'Pick a random topic and speak clearly on the spot. Use a quick structure to organize thoughts and avoid fillers.',
      activityImage: 'impromptu-topic-spinner',
      altText: 'Impromptu topic spinner',
    },
    {
      activityName: 'Presentation builder',
      activityDescription:
        'Create a mini presentation with a strong opening, two key points, and a clear closing. Practice smooth flow, confident voice, and simple gestures.',
      activityImage: 'presentation-builder',
      altText: 'Presentation builder',
    },
    {
      activityName: 'Q and A calm practice',
      activityDescription:
        'Learn to answer questions politely without freezing or rushing. Practice pausing, thinking, and responding clearly.',
      activityImage: 'q-and-a-calm-practice',
      altText: 'Q and A calm practice',
    },
    {
      activityName: 'Persuasion mini pitch',
      activityDescription:
        'Share an opinion with reasons and examples to persuade your audience. Learn confident delivery with a clear, organized structure.',
      activityImage: 'persuasion-mini-pitch',
      altText: 'Persuasion mini pitch',
    },
    {
      activityName: 'Agree and disagree',
      activityDescription:
        'Practice respectful agreement or disagreement using polite words. Support your opinion with simple logic and examples.',
      activityImage: 'agree-and-disagree',
      altText: 'Agree and disagree',
    },
    {
      activityName: 'Audience connection challenge',
      activityDescription:
        'Improve eye contact, gestures, and presence to keep listeners engaged. Learn to speak clearly while staying connected to your audience.',
      activityImage: 'audience-connection-challenge',
      altText: 'Audience connection challenge',
    },
    {
      activityName: 'Final showcase rehearsal',
      activityDescription:
        'Rehearse a complete speaking performance from start to finish. Use feedback to improve clarity, confidence, and stage presence.',
      activityImage: 'final-showcase-rehearsal',
      altText: 'Final showcase rehearsal',
    },
  ],
  PUBLIC_SPEAKING_GRADE_1_2: [
    {
      activityName: 'My confident hello',
      activityDescription:
        'Introduce yourself with a clear voice, smile, and strong posture. Practice eye contact and simple gestures to sound confident.',
      activityImage: 'my-confident-hello',
      altText: 'My confident hello',
    },
    {
      activityName: 'Quick talk challenge',
      activityDescription:
        'Share short ideas using simple, clear sentences. Think fast, speak smoothly, and build confidence step by step.',
      activityImage: 'quick-talk-challenge',
      altText: 'Quick talk challenge',
    },
    {
      activityName: 'Story steps adventure',
      activityDescription:
        'Create fun stories with a beginning, middle, and end. Use voice and expression to make your story come alive.',
      activityImage: 'story-steps-adventure',
      altText: 'Story steps adventure',
    },
    {
      activityName: 'Voice magic lab',
      activityDescription:
        'Try loud, soft, fast, and slow voices to control how you sound. Learn when to change pace and volume for better speaking.',
      activityImage: 'voice-magic-lab',
      altText: 'Voice magic lab',
    },
    {
      activityName: 'Feelings in action',
      activityDescription:
        'Show emotions using your voice, face, and body language. Practice making words sound happy, sad, excited, or calm.',
      activityImage: 'feelings-in-action',
      altText: 'Feelings in action',
    },
    {
      activityName: 'Spin and speak fun',
      activityDescription:
        'Pick a topic and share your thoughts quickly and clearly. Build confidence by organizing ideas on the spot.',
      activityImage: 'spin-and-speak-fun',
      altText: 'Spin and speak fun',
    },
    {
      activityName: 'Tiny talk builder',
      activityDescription:
        'Build a short talk with a clear start, main idea, and finish. Practice speaking smoothly in complete sentences.',
      activityImage: 'tiny-talk-builder',
      altText: 'Tiny talk builder',
    },
    {
      activityName: 'Calm answer corner',
      activityDescription:
        'Answer simple questions without getting nervous. Learn to pause, think, and respond clearly.',
      activityImage: 'calm-answer-corner',
      altText: 'Calm answer corner',
    },
    {
      activityName: 'My choice, my reason',
      activityDescription:
        'Share your choice and give one clear reason. Practice speaking confidently with simple logic.',
      activityImage: 'my-choice-my-reason',
      altText: 'My choice, my reason',
    },
    {
      activityName: 'Kind words circle',
      activityDescription:
        'Agree or disagree using polite, respectful words. Learn how to respond kindly in group talks.',
      activityImage: 'kind-words-circle',
      altText: 'Kind words circle',
    },
    {
      activityName: 'Look and connect games',
      activityDescription:
        'Practice eye contact while speaking clearly. Build confidence by looking up and staying connected.',
      activityImage: 'look-and-connect-games',
      altText: 'Look and connect games',
    },
    {
      activityName: 'Shine on stage practice',
      activityDescription:
        'Rehearse your speech before a final presentation. Improve confidence with practice and friendly feedback.',
      activityImage: 'shine-on-stage-practice',
      altText: 'Shine on stage practice',
    },
  ],
  CODING_GRADE_1_3: [
    {
      activityName: "Let's Bake a Cake!",
      activityDescription:
        'Kids arrange steps in the right order, just like a real recipe, to bake a virtual cake using sequencing logic.',
      activityImage: 'lets-bake-a-cake',
      altText: "Let's Bake a Cake!",
    },
    {
      activityName: 'Hungry casey-2',
      activityDescription:
        'Build a shooting game where kids aim and fire to feed a hungry character. Addictive and loads of fun.',
      activityImage: 'hungry-casey-2',
      altText: 'Hungry casey-2',
    },
    {
      activityName: 'Life Cycle of a Frog',
      activityDescription:
        "Click through each stage of a frog's life, from egg to tadpole to frog, with interactive animations.",
      activityImage: 'lifecycle-of-a-frog',
      altText: 'Life Cycle of a Frog',
    },
    {
      activityName: 'Dance Party',
      activityDescription:
        'Create a dance party with music, moving characters, and fun animations using block-based code.',
      activityImage: 'dance-party',
      altText: 'Dance Party',
    },
    {
      activityName: 'Health Guide App',
      activityDescription:
        'Design a simple app with buttons and images that shares healthy eating and lifestyle tips.',
      activityImage: 'health-guide-app',
      altText: 'Health Guide App',
    },
    {
      activityName: 'My Recipe Book App',
      activityDescription:
        'Tap a food item and the app shows its full recipe. Kids build their very own interactive cookbook.',
      activityImage: 'my-recipe-book-app',
      altText: 'My Recipe Book App',
    },
    {
      activityName: 'Meow Marvels',
      activityDescription:
        'Press a button and a random cat photo appears. Kids discover how real apps fetch and display live data.',
      activityImage: 'meow-marvels',
      altText: 'Meow Marvels',
    },
    {
      activityName: 'AI Birthday Wishes',
      activityDescription:
        'Build an AI assistant that translates a birthday message into multiple languages instantly.',
      activityImage: 'ai-birthday-wishes',
      altText: 'AI Birthday Wishes',
    },
    {
      activityName: 'Where Am I?',
      activityDescription:
        'Create a map app that shows your current location. Kids learn how GPS and real-world apps work.',
      activityImage: 'where-am-i',
      altText: 'Where Am I?',
    },
    {
      activityName: 'Space Shooter',
      activityDescription:
        'Build an action space game with movement controls, shooting mechanics, and real game logic.',
      activityImage: 'space-shooter',
      altText: 'Space Shooter',
    },
    {
      activityName: 'Snake and Food',
      activityDescription:
        'Recreate the classic Snake game. Collect food to grow your snake and avoid running into yourself.',
      activityImage: 'snake-and-food',
      altText: 'Snake and Food',
    },
    {
      activityName: 'Calculator',
      activityDescription:
        'Design and build a working calculator app from scratch. A great mix of math skills and coding logic.',
      activityImage: 'simple-calculator',
      altText: 'Calculator',
    },
  ],
  CODING_GRADE_6_8: [
    {
      activityName: 'My AI Friend GIGA',
      activityDescription:
        'Build a talking AI friend that remembers details, responds smartly, and helps with everyday tasks.',
      activityImage: 'my-ai-friend-giga',
      altText: 'My AI Friend GIGA',
    },
    {
      activityName: 'AI Dog Trainer',
      activityDescription:
        'Train an AI model to recognize hand gestures and make a Scratch dog respond to each command.',
      activityImage: 'ai-dog-trainer',
      altText: 'AI Dog Trainer',
    },
    {
      activityName: 'Face Filter Fun',
      activityDescription:
        'Build a fun face filter that tracks your face and adds an emoji mask in real time.',
      activityImage: 'face-filter-fun',
      altText: 'Face Filter Fun',
    },
    {
      activityName: 'Dance Party',
      activityDescription:
        'Build a playful bounce project using events and motion blocks. See how code makes objects move.',
      activityImage: 'dance-party',
      altText: 'Dance Party',
    },
    {
      activityName: 'Health Guide',
      activityDescription:
        'Place characters in the right spots and move them with confidence using position and direction in code.',
      activityImage: 'health-guide-app',
      altText: 'Health Guide',
    },
    {
      activityName: 'My Recipe Book App',
      activityDescription:
        'Use loops to repeat actions without rewriting the same blocks. A fun first step into smarter coding.',
      activityImage: 'my-recipe-book-app',
      altText: 'My Recipe Book App',
    },
    {
      activityName: 'Meow Marvels',
      activityDescription:
        'Explore conditionals by making projects react differently in different situations. Learn how code makes choices.',
      activityImage: 'meow-marvels',
      altText: 'Meow Marvels',
    },
    {
      activityName: 'AI Speaks Many Languages',
      activityDescription:
        'Build an interactive Play Lab game and learn how movement, control, and logic work together.',
      activityImage: 'ai-birthday-wishes',
      altText: 'AI Speaks Many Languages',
    },
    {
      activityName: 'Where Am I?',
      activityDescription:
        'Use Python to draw shapes and patterns. The perfect bridge from blocks to real code.',
      activityImage: 'where-am-i',
      altText: 'Where Am I?',
    },
    {
      activityName: 'Messenger App',
      activityDescription:
        'Design a chat-style app interface that explores messaging layouts and real user interaction patterns.',
      activityImage: 'messenger-app',
      altText: 'Messenger App',
    },
    {
      activityName: 'Space Explorer with ChatGPT',
      activityDescription:
        'Build a space-themed app experience that combines app prototyping with AI-powered exploration features.',
      activityImage: 'space-explorer-with-chatgpt',
      altText: 'Space Explorer with ChatGPT',
    },
    {
      activityName: 'Sign-in Screen App',
      activityDescription:
        'Design a polished sign-in screen with layout, inputs, and interface structure used in real-world apps.',
      activityImage: 'sign-in-screen-app',
      altText: 'Sign-in Screen App',
    },
  ],
  CODING_GRADE_9_10: [
    {
      activityName: 'My School Webpage',
      activityDescription:
        'Build a school-themed webpage using headings, paragraphs, and basic HTML structure elements.',
      activityImage: 'my-school-webpage',
      altText: 'My School Webpage',
    },
    {
      activityName: 'Registration Form',
      activityDescription:
        'Create a detailed registration form using input fields, dropdowns, and grouped sections for clear organization.',
      activityImage: 'registration-form',
      altText: 'Registration Form',
    },
    {
      activityName: 'Portfolio Website',
      activityDescription:
        'Design a personal portfolio site with carousel, scrollspy, and Bootstrap sections to showcase projects.',
      activityImage: 'portfolio-website',
      altText: 'Portfolio Website',
    },
    {
      activityName: 'Simple Calculator',
      activityDescription:
        'Build a working calculator using JavaScript variables, operators, functions, and real-time user interaction.',
      activityImage: 'simple-calculator',
      altText: 'Simple Calculator',
    },
    {
      activityName: 'Bank App',
      activityDescription:
        'Build a banking-style calculator app with structured layout, styling, and logic for financial operations.',
      activityImage: 'bank-app',
      altText: 'Bank App',
    },
    {
      activityName: 'Number Guessing Game',
      activityDescription:
        'Create an interactive Python game using lists, dictionaries, inputs, and logic to guess numbers smartly.',
      activityImage: 'number-guessing-game',
      altText: 'Number Guessing Game',
    },
    {
      activityName: 'Rock, Paper, Scissors GUI',
      activityDescription:
        'Design a graphical Python game with Tkinter widgets, images, buttons, and event-based interactions.',
      activityImage: 'rock-paper-scissors-gui',
      altText: 'Rock, Paper, Scissors GUI',
    },
    {
      activityName: 'New York Restaurant SQL Project',
      activityDescription:
        'Analyze restaurant data using SQL queries, filters, sorting, and aggregation to uncover useful insights.',
      activityImage: 'new-york-restaurant-sql-project',
      altText: 'New York Restaurant SQL Project',
    },
    {
      activityName: 'Weather Data Visualization',
      activityDescription:
        'Turn raw weather datasets into meaningful graphs using Python plotting libraries and data visualization tools.',
      activityImage: 'weather-data-visualization',
      altText: 'Weather Data Visualization',
    },
    {
      activityName: 'Titanic Survival Prediction',
      activityDescription:
        'Explore statistics, data transformation, and exploratory analysis through a project built on Titanic data.',
      activityImage: 'titanic-survival-prediction',
      altText: 'Titanic Survival Prediction',
    },
    {
      activityName: 'Recommendation Engine Model',
      activityDescription:
        'Build a basic recommendation engine that suggests relevant outcomes using machine learning concepts.',
      activityImage: 'recommendation-engine-model',
      altText: 'Recommendation Engine Model',
    },
    {
      activityName: 'Weather Prediction App',
      activityDescription:
        'Create a Flask-powered weather app combining Python logic, web forms, and dynamic output display.',
      activityImage: 'weather-prediction-app',
      altText: 'Weather Prediction App',
    },
  ],
  CODING_GRADE_11_12: [
    {
      activityName: 'My School Webpage',
      activityDescription:
        'Build a school-themed webpage using headings, paragraphs, and basic HTML structure elements.',
      activityImage: 'my-school-webpage',
      altText: 'My School Webpage',
    },
    {
      activityName: 'Registration Form',
      activityDescription:
        'Create a detailed registration form using input fields, dropdowns, and grouped sections for clear organization.',
      activityImage: 'registration-form',
      altText: 'Registration Form',
    },
    {
      activityName: 'Portfolio Website',
      activityDescription:
        'Design a personal portfolio site with carousel, scrollspy, and Bootstrap sections to showcase projects.',
      activityImage: 'portfolio-website',
      altText: 'Portfolio Website',
    },
    {
      activityName: 'Rainbow Spiral with Turtle',
      activityDescription:
        'Use Python Turtle to generate colorful spiral patterns while learning loops and drawing logic.',
      activityImage: 'rainbow-spiral-with-turtle',
      altText: 'Rainbow Spiral with Turtle',
    },
    {
      activityName: 'Bank App Calculator',
      activityDescription:
        'Build a styled banking calculator app with structured layout design and real calculation functionality.',
      activityImage: 'bank-app',
      altText: 'Bank App Calculator',
    },
    {
      activityName: 'Number Guessing Game',
      activityDescription:
        'Create an interactive Python game using lists, dictionaries, inputs, and logic to guess numbers smartly.',
      activityImage: 'number-guessing-game',
      altText: 'Number Guessing Game',
    },
    {
      activityName: 'Rock, Paper, Scissors GUI',
      activityDescription:
        'Design a graphical Python game with Tkinter widgets, images, buttons, and event-based interactions.',
      activityImage: 'rock-paper-scissors-gui',
      altText: 'Rock, Paper, Scissors GUI',
    },
    {
      activityName: 'Iris Dataset Analysis',
      activityDescription:
        'Analyze a real dataset through a capstone project covering data cleaning, visualization, and interpretation.',
      activityImage: 'iris-dataset-analysis',
      altText: 'Iris Dataset Analysis',
    },
    {
      activityName: 'Weather Data Visualization',
      activityDescription:
        'Turn raw weather datasets into meaningful graphs using Python plotting libraries and data visualization tools.',
      activityImage: 'weather-data-visualization',
      altText: 'Weather Data Visualization',
    },
    {
      activityName: 'Codingal Banking Services',
      activityDescription:
        'Build a complete Java banking project combining object-oriented programming with practical application flow.',
      activityImage: 'codingal-bank-services',
      altText: 'Codingal Banking Services',
    },
    {
      activityName: 'Recommendation Engine Model',
      activityDescription:
        'Build a basic recommendation engine that suggests relevant outcomes using machine learning concepts.',
      activityImage: 'recommendation-engine-model',
      altText: 'Recommendation Engine Model',
    },
    {
      activityName: 'Weather Prediction App',
      activityDescription:
        'Create a Flask-powered weather app combining Python logic, web forms, and dynamic output display.',
      activityImage: 'weather-prediction-app',
      altText: 'Weather Prediction App',
    },
  ],
  CODING_GRADE_4_5: [
    {
      activityName: 'Astro Rescue',
      activityDescription:
        'Arrange steps in the right order to help astronauts get home safely. Build logic and sequencing through gameplay.',
      activityImage: 'astro-rescue',
      altText: 'Astro Rescue',
    },
    {
      activityName: 'Pawsome',
      activityDescription:
        'Click for a surprise. Build an app that displays random dog images using imported data and button clicks.',
      activityImage: 'pawsome',
      altText: 'Pawsome',
    },
    {
      activityName: 'Microscope',
      activityDescription:
        'Zoom in with code and explore the microscopic world through a science-themed Scratch project.',
      activityImage: 'microscope',
      altText: 'Microscope',
    },
    {
      activityName: 'Pacman',
      activityDescription:
        'Code the chase and clear the maze. Build a Scratch game using movement, controls, and maze-based gameplay.',
      activityImage: 'pacman',
      altText: 'Pacman',
    },
    {
      activityName: 'AI Rock-Paper-Scissors',
      activityDescription:
        'Show your move and let AI decide. Train a model to recognize hand gestures in an interactive Scratch game.',
      activityImage: 'ai-rock-paper-scissors',
      altText: 'AI Rock-Paper-Scissors',
    },
    {
      activityName: 'My Recipe Book App',
      activityDescription:
        'Build a recipe-themed project using loops to repeat actions and take first steps into smarter coding.',
      activityImage: 'my-recipe-book-app',
      altText: 'My Recipe Book App',
    },
    {
      activityName: 'AI Mailman',
      activityDescription:
        'Train an AI to sort mail correctly. Build a Scratch project that matches city codes to the right delivery truck.',
      activityImage: 'ai-mailman',
      altText: 'AI Mailman',
    },
    {
      activityName: 'AI Speaks Many Languages',
      activityDescription:
        'Build an AI project that translates messages across multiple languages using trained models and Scratch.',
      activityImage: 'ai-birthday-wishes',
      altText: 'AI Speaks Many Languages',
    },
    {
      activityName: 'Where Am I?',
      activityDescription:
        'Take first steps into typed coding. Build a Python map-based project and bridge block coding to real code.',
      activityImage: 'where-am-i',
      altText: 'Where Am I?',
    },
    {
      activityName: 'Portfolio Website',
      activityDescription:
        'Build a personal portfolio website to showcase your work using HTML, CSS, and Bootstrap basics.',
      activityImage: 'portfolio-website',
      altText: 'Portfolio Website',
    },
    {
      activityName: 'Phonebook Using Python',
      activityDescription:
        'Build a phonebook project while applying Python fundamentals and object-oriented programming concepts.',
      activityImage: 'phonebook-using-python',
      altText: 'Phonebook Using Python',
    },
    {
      activityName: 'To-Do List App',
      activityDescription:
        'Build a fully functional to-do list app from scratch using HTML, CSS, Bootstrap, and JavaScript.',
      activityImage: 'to-do-list-app',
      altText: 'To-Do List App',
    },
  ],
}

type Props = {
  title: string
  courseTitle: string
  description?: string
  urlQuery?: string
}

const ExpandableActivities = ({
  title,
  courseTitle,
  description,
  urlQuery,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const visibleActivities = isExpanded
    ? ACTIVITIES[courseTitle]
    : ACTIVITIES[courseTitle].slice(0, INITIAL_VISIBLE_COUNT)

  const hasMore = ACTIVITIES[courseTitle].length > INITIAL_VISIBLE_COUNT

  return (
    <div className="my-10 lg:px-3 py-5 flex flex-col text-grey">
      <h2 className="text-2xl font-600 leading-tight mb-4">{title}</h2>
      {description && <p className="lg:text-lg mb-5">{description}</p>}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 justify-center">
        {visibleActivities.map((activity) => (
          <div
            key={activity.activityName}
            className="max-w-sm mx-auto h-full"
            style={{ maxWidth: '250px' }}
          >
            <div className="rounded-md shadow-md h-full flex flex-col overflow-hidden bg-white">
              <FastlyImage
                src={`${IMAGE_PATH}${activity.activityImage}.jpg`}
                width={250}
                height={169}
                objectFit="fill"
                alt={activity.altText || activity.activityName}
              />
              <div className="p-2 sm:p-3 flex flex-col flex-1">
                <h3 className="text-lg font-600 mb-1 leading-tight">
                  {activity.activityName}
                </h3>
                <p className="text-grey-800 leading-snug text-sm">
                  {activity.activityDescription}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="mx-auto mt-6">
          <Button
            variant="outline"
            intent="primary"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded
              ? 'Show fewer activities'
              : `View all ${
                  ACTIVITIES[courseTitle].length - INITIAL_VISIBLE_COUNT
                }+ activities`}
          </Button>
        </div>
      )}

      <div className="mx-auto w-full md:max-w-xs mt-10 text-center">
        <BookFreeClassBtn query={urlQuery} />
      </div>
    </div>
  )
}

export default ExpandableActivities
